import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Button,
  Stack,
  Chip,
  Tooltip
} from '@mui/material';
import {CodeBlock} from "react-code-blocks";
import TimerIcon from '@mui/icons-material/Timer';
import { sectionIcon } from '../testSections';

export default function TestQuestion({ 
  question, 
  currentIndex, 
  totalQuestions, 
  onAnswer, 
  onNext,
  timePerQuestion = 60,
  sectionLabel = 'Question',
  sectionKey,
  isLastQuestion = false
}) {
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(timePerQuestion);
  const skipTriggeredRef = useRef(false);

  useEffect(() => {
    setTimeLeft(timePerQuestion);
    setSelectedAnswer('');
    skipTriggeredRef.current = false;
  }, [currentIndex, timePerQuestion]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      onNext();
    }
  }, [timeLeft, onNext]);

  useEffect(() => {
    const triggerSkipOnce = () => {
      if (skipTriggeredRef.current) return;
      skipTriggeredRef.current = true;
      onAnswer('skipped');
      onNext();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        triggerSkipOnce();
      }
    };

    const handleWindowBlur = () => {
      triggerSkipOnce();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onAnswer, onNext, currentIndex]);

  const handleAnswerChange = (event) => {
    setSelectedAnswer(event.target.value);
  };

  const handleNext = () => {
    onAnswer(selectedAnswer);
    onNext();
  };

  const handleSkip = () => {
    onAnswer('skipped');
    onNext();
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Box 
        component="img"
        src="/marmin_logo.png"
        alt="Marmin Logo"
        sx={{ 
          height: 60,
          mb: 3,
          display: 'block',
          mx: 'auto'
        }}
      />
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              icon={sectionIcon(sectionKey)}
              label={sectionLabel}
              color={sectionKey === 'programming' ? 'secondary' : 'primary'}
              variant="outlined"
              sx={{ fontSize: 16 }}
            />
            <Typography variant="h6" color="text.secondary">
              Question {currentIndex + 1} of {totalQuestions}
            </Typography>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Time remaining for this question">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon color="primary" />
                <Typography variant="body2" color="primary" fontWeight={500}>
                  {timeLeft}s
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/* Progress Bar */}
        <LinearProgress 
          variant="determinate" 
          value={(timeLeft / timePerQuestion) * 100} 
          sx={{ 
            mb: 4,
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4
            }
          }}
        />

        {/* Question Content */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 3,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              whiteSpace: 'pre-line'
            }}
            dangerouslySetInnerHTML={{ __html: question.question }}
          />
          {question.program && (
            <Box sx={{ 
              mb: 3,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}>
              <CodeBlock
                text={question.program}
                language={'java'}
                showLineNumbers={true}
                wrapLines
              />
            </Box>
          )}
        </Box>

        {/* Answer Options */}
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={selectedAnswer}
            onChange={handleAnswerChange}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={
                  <Radio 
                    sx={{
                      '&.Mui-checked': {
                        color: 'primary.main',
                      }
                    }}
                  />
                }
                label={
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      ml: 1,
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                  >
                    {option}
                  </Typography>
                }
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: selectedAnswer === option ? 'primary.main' : 'grey.200',
                  bgcolor: selectedAnswer === option ? 'primary.light' : 'transparent',
                  '&:hover': {
                    bgcolor: 'grey.50'
                  }
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Action Buttons */}
        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: 2
        }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSkip}
            sx={{
              minWidth: 100,
              textTransform: 'none'
            }}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!selectedAnswer}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            {isLastQuestion ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
} 
